import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import {
    Button,
    GhostButton,
    gtagEvent,
    gtagIdentifyOwner,
    gtagIdentifyWorker,
    gtagClearIdentity,
    vars,
    getTokenFromUrl,
    Badge,
} from '@agendor/ui';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import BookingsSection from './sections/BookingsSection';
import ClientsSection from './sections/ClientsSection';
import ServicesSection from './sections/ServicesSection';
import WorkersSection from './sections/WorkersSection';
import StatsSection from './sections/StatsSection';
import AssistantSection, { AiBudgetState } from './sections/AssistantSection';
import { AiBudgetApi } from '@agendor/shared';
import PaymentsSection from './sections/PaymentsSection';
import ConfigurationSection from './sections/ConfigurationSection';
import CreditPurchaseModal from './sections/CreditPurchaseModal';
import ConnectorAuthorizationDialog from './components/ConnectorAuthorizationDialog';
import { clearPendingConnectorCode, isConnectorOfflineForLong, readPendingConnectorCode } from './whatsapp-connector';
import { useVisibleInterval } from './use-visible-interval';
import { SectionVisibilityProvider } from './sections/section-visibility-context';
import BusinessService, {
    BusinessDetails,
    Client,
    hasConnectorAccess,
    hasPaidAccess,
    isSameBusinessDetails,
} from './business-service';
import AuthService from './auth-service';
import { buildPath, parseRouteFromPath, View, ParsedRoute } from './navigation';
import { ToastProvider, useToast } from './toast-context';
import LoginScreen from './LoginScreen';
import { daysFromToday } from './date-utils';

const GlobalStyle = createGlobalStyle`
    :root {
        font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        color: ${vars.foregroundColor};
    }

    *, *::before, *::after {
        box-sizing: border-box;
    }

    html, body, #root {
        margin: 0;
        padding: 0;
        min-height: 100%;
    }

    body {
        background-color: ${vars.backgroundColorDark};
    }
`;

const Page = styled.div`
    min-height: 100vh;
    display: flex;
    flex-direction: column;

    @media (max-width: 600px) {
        min-height: 100dvh;
    }
`;

const MainContainer = styled.div`
    display: flex;
    flex: 1;
    width: 100%;
    position: relative;
    background-color: ${vars.backgroundColor};
`;

const Sidebar = styled.aside<{ isOpen: boolean }>`
    width: 250px;
    background-color: ${vars.backgroundColorBright};
    border-right: 1px solid ${vars.dividerColor};
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: none;
    flex-shrink: 0;

    @media (max-width: 768px) {
        z-index: 15;
        position: fixed;
        top: ${vars.navbarHeight};
        left: 0;
        bottom: 0;
        width: 100%;
        transform: translateX(${(p) => (p.isOpen ? '0' : '-100%')});
        border-right: none;
        background-color: ${vars.backgroundColorBright};
        min-height: 100%;
    }
`;

const Content = styled.main`
    flex: 1;
    padding: 20px;
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
`;

const ContentArea = styled.div`
    flex: 1;
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;
`;

const SectionWrapper = styled.div<{ visible: boolean }>`
    display: ${(p) => (p.visible ? 'block' : 'none')};
`;

const Banner = styled.div<{ variant?: 'warning' | 'error' }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    margin: 0 auto 12px;
    max-width: 800px;
    background-color: ${(p) => (p.variant === 'error' ? vars.dangerTintSoft : vars.warningTintSoft)};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    font-size: 0.9rem;
    color: ${vars.foregroundColor};

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const BannerContent = styled.span`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const BannerTitle = styled.span`
    font-weight: 600;
`;

const BannerButtons = styled.div`
    display: flex;
    gap: 8px;

    @media (max-width: 600px) {
        justify-content: flex-end;
    }
`;

const BannersContainer = styled.div`
    padding-bottom: 8px;

    &:empty {
        display: none;
    }
`;

function SectionContainer({ visible, children }: { visible: boolean; children: React.ReactNode }) {
    return (
        <SectionVisibilityProvider visible={visible}>
            <SectionWrapper visible={visible} aria-hidden={!visible}>
                {children}
            </SectionWrapper>
        </SectionVisibilityProvider>
    );
}

const NavItem = styled.button<{ active: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 12px 16px;
    border: 1px solid ${(p) => (p.active ? vars.dividerColor : 'transparent')};
    border-radius: 6px;
    background-color: ${(p) => (p.active ? vars.primaryColorLight : 'transparent')};
    color: ${vars.foregroundColor};
    font-weight: 400;
    font-size: 1rem;
    text-align: left;
    cursor: pointer;

    &:hover {
        background-color: ${(p) => (p.active ? vars.primaryColorLight : vars.backgroundColorBright)};
        border-color: ${(p) => (p.active ? vars.dividerColor : vars.dividerColor)};
    }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
    display: none;
    @media (max-width: 768px) {
        display: block;
        position: fixed;
        top: ${vars.navbarHeight};
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        opacity: ${(p) => (p.isOpen ? 1 : 0)};
        pointer-events: ${(p) => (p.isOpen ? 'auto' : 'none')};
        transition: none;
        z-index: 14;
    }
`;

const LoginPage = styled.div`
    min-height: 100vh;
    background: ${vars.backgroundColor};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
    color: ${vars.foregroundColor};
    position: relative;

    @media (max-width: 600px) {
        min-height: 100dvh;
        padding: 0;
        align-items: stretch;
    }
`;

const LoginCard = styled.div`
    position: relative;
    background: ${vars.backgroundColorBright};
    border: 1px solid ${vars.dividerColor};
    border-radius: 6px;
    width: min(560px, 100%);
    display: flex;
    flex-direction: column;
    gap: 0;
    z-index: 1;

    @media (max-width: 600px) {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        border: none;
        border-radius: 0;
    }
`;

const CardNavbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(${vars.dangerTintSoftRgb}, 0.95);
    border-bottom: 1px solid ${vars.dividerColor};
    border-radius: 6px 6px 0 0;
    padding: 10px 12px;
    gap: 8px;

    @media (max-width: 600px) {
        height: ${vars.navbarHeight};
        padding: 0 24px;
        border-radius: 0;
    }

    @media (max-width: 480px) {
        padding: 0 16px;
    }
`;

const BrandGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const BrandLink = styled(GhostButton)`
    gap: 8px;
    font-weight: 600;
    font-size: 0.95rem;
`;

const BrandLogo = styled.img`
    display: block;
    width: 24px;
    height: 24px;
`;

const SupportLink = styled(GhostButton)``;

const CardBody = styled.div`
    padding: 24px 32px 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const appPhoneNumberKey = 'REACT_APP_APP_PHONE_NUMBER';
const appPhoneNumber = process.env[appPhoneNumberKey];
if (appPhoneNumber === undefined) {
    throw new Error(`No value found for environment value "${appPhoneNumberKey}"`);
}

const serverRootPathKey = 'REACT_APP_SERVER_ROOT_PATH';
const _serverRootPath = (window as any).__SERVER_ROOT_PATH__ ?? process.env[serverRootPathKey];
if (_serverRootPath === undefined) {
    throw new Error(`No value found for environment value "${serverRootPathKey}"`);
}
const serverRootPath = _serverRootPath;

// How long the panel keeps watching for an approved computer to come online.
const connectorApprovalWatchMs = 10 * 60_000;

const _authService = new AuthService({ serverRootPath, initialAccessToken: getTokenFromUrl() });
const _service = new BusinessService({ serverRootPath, authService: _authService });

function useClients(service: BusinessService | null) {
    const [clients, setClients] = useState<Client[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const attemptedRef = useRef(false);

    const load = useCallback(async () => {
        if (!service) return;
        setIsLoading(true);
        setLoadError(null);
        attemptedRef.current = true;
        try {
            const data = await service.getClients();
            setClients(data);
        } catch (error) {
            console.error('Failed to load clients', error);
            setLoadError('Não foi possível carregar os clientes.');
        } finally {
            setIsLoading(false);
        }
    }, [service]);

    const ensureLoaded = useCallback(() => {
        if (attemptedRef.current || isLoading) return;
        load();
    }, [isLoading, load]);

    useEffect(() => {
        attemptedRef.current = false;
        setClients([]);
        setLoadError(null);
    }, [service]);

    return { clients, isLoading, loadError, ensureLoaded, reload: load };
}

function AuthenticatedApp({
    onSignOut,
    whatsappOnboardingConfigId,
    initialBusiness,
}: {
    onSignOut: () => void;
    whatsappOnboardingConfigId: string;
    initialBusiness: BusinessDetails;
}) {
    const [isAuthenticated, setIsAuthenticated] = useState(_authService.isAuthenticated);
    const [business, setBusiness] = useState<BusinessDetails | null>(initialBusiness);
    const [businessLoadError, setBusinessLoadError] = useState<string | null>(null);
    const [isLoadingBusiness, setIsLoadingBusiness] = useState(false);
    const [route, setRoute] = useState<ParsedRoute>(() => parseRouteFromPath(window.location.pathname));
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const basePath = route.basePath;
    const currentView = route.view;

    const isOwner = business !== null && business.userLevel !== 'worker';
    const currentWorkerId = business?.workerId ?? null;

    const [aiBudgetState, setAiBudgetState] = useState<AiBudgetState>({ status: 'loading' });
    const [pendingConnectorCode, setPendingConnectorCode] = useState(readPendingConnectorCode);
    const [connectorApprovedAt, setConnectorApprovedAt] = useState<number | null>(null);
    const { showToast } = useToast();

    const closeConnectorAuthorization = () => {
        clearPendingConnectorCode();
        setPendingConnectorCode(null);
    };

    const {
        clients,
        isLoading: isLoadingClients,
        loadError: clientsLoadError,
        ensureLoaded: ensureClientsLoaded,
        reload: reloadClients,
    } = useClients(isAuthenticated ? _service : null);

    const isSubscriptionInactive = business !== null && !hasPaidAccess(business);

    const navigateToUpgrade = useCallback(() => {
        window.history.pushState({}, '', '/configuration/plan?upgrade=solo');
        window.dispatchEvent(new PopStateEvent('popstate'));
    }, []);

    const handleReactivateClick = () => {
        gtagEvent('subscription_inactive_banner_click');
        pushRoute('configuration', ['plan'], false);
        setTimeout(() => {
            window.history.replaceState({}, '', '/configuration/plan?upgrade=solo');
            window.dispatchEvent(new PopStateEvent('popstate'));
        }, 0);
    };

    const loadAiBudget = useCallback(async () => {
        if (!isAuthenticated) return;
        setAiBudgetState({ status: 'loading' });
        try {
            const snapshot = await _service.getAiBudget();
            // Server contract: when the bool says we're budgeted, /ai-budget returns a snapshot.
            // A null here means the bool and the snapshot disagree (rare race during plan changes) —
            // surface as error so the user can retry; a fresh business reload will sync the bool.
            if (snapshot === null) {
                console.error('AI budget snapshot is null despite hasAiBudget=true');
                setAiBudgetState({ status: 'error' });
                return;
            }
            setAiBudgetState({ status: 'loaded', snapshot, lastUpdatedAt: Date.now() });
        } catch (error) {
            console.error('Failed to load AI budget', error);
            setAiBudgetState({ status: 'error' });
        }
    }, [isAuthenticated]);

    const refreshAiBudget = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const snapshot = await _service.getAiBudget();
            if (snapshot === null) {
                console.error('AI budget snapshot is null despite hasAiBudget=true');
                showToast('Não foi possível atualizar os créditos.', 'error');
                return;
            }
            setAiBudgetState({ status: 'loaded', snapshot, lastUpdatedAt: Date.now() });
        } catch (error) {
            console.error('Failed to refresh AI budget', error);
            showToast('Não foi possível atualizar os créditos.', 'error');
        }
    }, [isAuthenticated, showToast]);

    const hasAiBudget = business?.subscription?.hasAiBudget ?? false;
    useEffect(() => {
        if (hasAiBudget) {
            loadAiBudget();
        } else {
            setAiBudgetState({ status: 'loading' });
        }
    }, [hasAiBudget, loadAiBudget]);

    const showCreditsDepletedBanner =
        isOwner && aiBudgetState.status === 'loaded' && AiBudgetApi.isSuspended(aiBudgetState.snapshot);
    const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);

    const showConnectWhatsAppBanner = React.useMemo(() => {
        if (!business || !hasPaidAccess(business)) return false;
        return business.whatsapp === null;
    }, [business]);

    const showWhatsAppInvalidatedBanner = React.useMemo(() => {
        const whatsapp = business?.whatsapp;
        if (!whatsapp || whatsapp.type === 'test_mode') return false;
        return whatsapp.invalidated;
    }, [business]);

    const whatsAppBillingBlock = React.useMemo(() => {
        const whatsapp = business?.whatsapp;
        if (whatsapp?.type !== 'cloud_api' || whatsapp.invalidated || !whatsapp.billingBlocked) return null;
        return { billingUrl: whatsapp.billingUrl };
    }, [business]);

    // The banner stays until the next load, so the owner can go back to Meta if adding the card didn't work.
    const handleWhatsAppBillingBlockClick = () => {
        gtagEvent('whatsapp_billing_block_banner_click');
        _service.clearWhatsAppBillingBlock().catch((error) => {
            console.error('Failed to clear the WhatsApp billing block.', error);
        });
    };

    const canUseConnector = business !== null && hasConnectorAccess(business);
    const showConnectorOfflineBanner =
        canUseConnector &&
        business.whatsapp?.type === 'connector' &&
        !business.whatsapp.invalidated &&
        business.whatsappConnector != null &&
        isConnectorOfflineForLong(business.whatsappConnector, Date.now());
    // A business with WhatsApp but without the connector never hears of it; one without WhatsApp
    // is told its plan doesn't include it.
    const offersConnectorAuthorization = business !== null && (canUseConnector || !hasPaidAccess(business));
    const dropsConnectorCode = pendingConnectorCode !== null && business !== null && !offersConnectorAuthorization;
    useEffect(() => {
        if (!dropsConnectorCode) return;
        clearPendingConnectorCode();
        setPendingConnectorCode(null);
    }, [dropsConnectorCode]);

    const handleConnectWhatsAppClick = () => {
        gtagEvent('connect_whatsapp_banner_click');
        pushRoute('assistant', ['config'], false);
    };

    const handleReconnectWhatsAppClick = () => {
        gtagEvent('reconnect_whatsapp_banner_click');
        pushRoute('assistant', ['config'], false);
    };

    // Calcular dias até vencimento ou dias vencido
    const subscriptionBannerInfo = React.useMemo(() => {
        if (!business?.subscription) return null;

        const { nextBillingDate, isTrial, invoiceUrl } = business.subscription;
        const diffDays = daysFromToday(nextBillingDate);
        const gracePeriodDays = 7;

        if (diffDays >= 0) {
            // Ainda não venceu
            if (isTrial && diffDays <= 7) {
                return {
                    type: 'trial_ending' as const,
                    daysRemaining: diffDays,
                    invoiceUrl,
                };
            }
            return null;
        } else {
            // Vencido
            const daysOverdue = Math.abs(diffDays);
            const daysUntilShutdown = gracePeriodDays - daysOverdue;
            if (daysUntilShutdown > 0) {
                return {
                    type: 'overdue' as const,
                    daysOverdue,
                    daysUntilShutdown,
                    invoiceUrl,
                };
            }
            return null; // Já foi suspenso, não deveria chegar aqui
        }
    }, [business?.subscription]);

    const loadBusiness = React.useCallback(async () => {
        if (!isAuthenticated) return;
        setIsLoadingBusiness(true);
        try {
            const data = await _service.getBusiness();
            setBusiness(data);
            setBusinessLoadError(null);
        } catch (error) {
            console.error('Failed to load business details', error);
            setBusiness(null);
            setBusinessLoadError('Não foi possível carregar as informações do negócio.');
        } finally {
            setIsLoadingBusiness(false);
        }
    }, [isAuthenticated]);

    // Background refresh: keeps the last good business on failure and skips overlapping requests.
    const refreshInFlight = useRef<Promise<void> | null>(null);
    const refreshBusiness = React.useCallback((): Promise<void> => {
        if (!isAuthenticated) return Promise.resolve();
        if (refreshInFlight.current) return refreshInFlight.current;
        const request = (async () => {
            try {
                const data = await _service.getBusiness();
                setBusiness((previous) => (previous && isSameBusinessDetails(previous, data) ? previous : data));
            } catch (error) {
                console.error('Failed to refresh business details', error);
            } finally {
                refreshInFlight.current = null;
            }
        })();
        refreshInFlight.current = request;
        return request;
    }, [isAuthenticated]);

    const watchesConnector =
        isOwner && canUseConnector && (business.whatsapp?.type === 'connector' || business.whatsappConnector != null);
    useVisibleInterval(refreshBusiness, { enabled: watchesConnector, intervalMs: 60_000, immediate: false });

    useEffect(() => {
        if (connectorApprovedAt === null) return;
        const timeout = setTimeout(() => setConnectorApprovedAt(null), connectorApprovalWatchMs);
        return () => clearTimeout(timeout);
    }, [connectorApprovedAt]);

    const hasPairedConnector = business?.whatsappConnector != null;
    useEffect(() => {
        if (hasPairedConnector) setConnectorApprovedAt(null);
    }, [hasPairedConnector]);

    const pushRoute = (view: View, modalSegments: string[] = [], replace = false) => {
        const path = buildPath(basePath, view, modalSegments).replace(/\/{2,}/g, '/');
        const method: 'pushState' | 'replaceState' = replace ? 'replaceState' : 'pushState';
        window.history[method](null, '', path);
        setRoute({ basePath, view, modalSegments });
    };

    useEffect(() => {
        const subscription = _service.onInvalidToken.subscribe(() => {
            setIsAuthenticated(false);
        });
        return () => subscription.unsubscribe();
    }, []);

    const initialLoadDone = useRef(true);
    useEffect(() => {
        if (!isAuthenticated) {
            setBusiness(null);
            setBusinessLoadError(null);
            setIsLoadingBusiness(false);
            initialLoadDone.current = false;
            return;
        }

        if (initialLoadDone.current) {
            initialLoadDone.current = false;
            return;
        }

        let cancelled = false;
        setIsLoadingBusiness(true);
        setBusinessLoadError(null);
        const loadBusiness = async () => {
            try {
                const data = await _service.getBusiness();
                if (!cancelled) {
                    setBusiness(data);
                    setBusinessLoadError(null);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Failed to load business details', error);
                    setBusiness(null);
                    setBusinessLoadError('Não foi possível carregar as informações do negócio.');
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingBusiness(false);
                }
            }
        };

        loadBusiness();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);

    useEffect(() => {
        const url = new URL(window.location.href);
        url.pathname = buildPath(basePath, route.view, route.modalSegments).replace(/\/{2,}/g, '/');
        window.history.replaceState(null, '', url.toString());
    }, [basePath, route]);

    useEffect(() => {
        const onPopState = () => {
            setRoute(parseRouteFromPath(window.location.pathname));
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    // Route guard: redirect workers away from owner-only views
    const ownerOnlyViews: View[] = ['services', 'workers', 'stats', 'assistant', 'payments'];
    useEffect(() => {
        if (!isOwner && ownerOnlyViews.includes(currentView)) {
            pushRoute('bookings', [], true);
        }
    }, [isOwner, currentView]);

    // Track page views on route changes
    const viewTitles: Record<View, string> = {
        bookings: 'Agendamentos',
        services: 'Serviços',
        workers: 'Profissionais',
        clients: 'Clientes',
        stats: 'Estatísticas',
        assistant: 'Assistente',
        payments: 'Pagamentos',
        configuration: 'Configurações',
    };
    useEffect(() => {
        const pageTitle = `OrganizaBot Gestão | ${viewTitles[route.view]}`;
        document.title = pageTitle;
        gtagEvent('page_view', {
            page_title: pageTitle,
            page_location: window.location.href,
            page_path: window.location.pathname,
            source_type: 'manual',
        });
        if (typeof window.fbq === 'function') {
            window.fbq('track', 'PageView');
        }
    }, [route.view]);

    const handleMenuClick = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleBrandClick = () => {
        pushRoute('bookings');
        setIsSidebarOpen(false);
    };

    const handleNavClick = (view: View) => {
        gtagEvent('nav_section_click', { nav_target: view });
        pushRoute(view);
        setIsSidebarOpen(false);
    };

    const handleSignOut = async () => {
        await _authService.logout();
        setIsAuthenticated(false);
        onSignOut();
    };

    if (!business) return null;

    return (
        <Page>
            <Navbar onMenuClick={handleMenuClick} onBrandClick={handleBrandClick} isOpen={isSidebarOpen} />
            <MainContainer>
                <Overlay isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(false)} />
                <Sidebar isOpen={isSidebarOpen}>
                    <NavItem
                        data-testid="nav-bookings"
                        active={currentView === 'bookings'}
                        onClick={() => handleNavClick('bookings')}
                    >
                        Agendamentos
                    </NavItem>
                    {isOwner && (
                        <NavItem
                            data-testid="nav-services"
                            active={currentView === 'services'}
                            onClick={() => handleNavClick('services')}
                        >
                            Serviços
                        </NavItem>
                    )}
                    {isOwner && (
                        <NavItem
                            data-testid="nav-workers"
                            active={currentView === 'workers'}
                            onClick={() => handleNavClick('workers')}
                        >
                            Profissionais
                        </NavItem>
                    )}
                    <NavItem
                        data-testid="nav-clients"
                        active={currentView === 'clients'}
                        onClick={() => handleNavClick('clients')}
                    >
                        Clientes
                    </NavItem>
                    {isOwner && (
                        <NavItem
                            data-testid="nav-stats"
                            active={currentView === 'stats'}
                            onClick={() => handleNavClick('stats')}
                        >
                            Estatísticas
                        </NavItem>
                    )}
                    {isOwner && (
                        <NavItem
                            data-testid="nav-assistant"
                            active={currentView === 'assistant'}
                            onClick={() => handleNavClick('assistant')}
                        >
                            Assistente
                        </NavItem>
                    )}
                    {isOwner && (
                        <NavItem
                            data-testid="nav-payments"
                            active={currentView === 'payments'}
                            onClick={() => handleNavClick('payments')}
                        >
                            Pagamentos
                        </NavItem>
                    )}
                    <NavItem
                        data-testid="nav-settings"
                        active={currentView === 'configuration'}
                        onClick={() => handleNavClick('configuration')}
                    >
                        Configurações
                    </NavItem>
                </Sidebar>
                <Content>
                    <ContentArea>
                        <BannersContainer>
                            {isOwner && subscriptionBannerInfo?.type === 'trial_ending' && (
                                <Banner data-testid="banner-trial">
                                    <BannerContent>
                                        <BannerTitle>
                                            ⚠️{' '}
                                            {subscriptionBannerInfo.daysRemaining === 0
                                                ? 'Seu teste grátis acaba a qualquer momento'
                                                : `Seu teste grátis acaba em ${subscriptionBannerInfo.daysRemaining} ${subscriptionBannerInfo.daysRemaining === 1 ? 'dia' : 'dias'}`}
                                        </BannerTitle>
                                        <span>
                                            Pague a cobrança para manter seu plano. Depois disso, seus clientes não
                                            agendam mais pelo link nem recebem lembretes.
                                        </span>
                                    </BannerContent>
                                    <BannerButtons>
                                        {subscriptionBannerInfo.invoiceUrl && (
                                            <Button
                                                as="a"
                                                href={subscriptionBannerInfo.invoiceUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={() =>
                                                    gtagEvent('banner_pay_click', { source: 'trial_ending' })
                                                }
                                            >
                                                Pagar cobrança
                                            </Button>
                                        )}
                                    </BannerButtons>
                                </Banner>
                            )}
                            {showCreditsDepletedBanner && aiBudgetState.status === 'loaded' && (
                                <Banner variant="error" data-testid="banner-credits-depleted">
                                    <BannerContent>
                                        <BannerTitle>🤖 Assistente de IA pausado</BannerTitle>
                                        <span>
                                            Seus créditos de IA acabaram. Compre uma recarga para reativar agora, ou
                                            aguarde{' '}
                                            {(() => {
                                                const days = Math.max(
                                                    0,
                                                    daysFromToday(aiBudgetState.snapshot.cycleEndsAt),
                                                );
                                                if (days === 0) return 'o próximo ciclo (hoje)';
                                                if (days === 1) return '1 dia';
                                                return `${days} dias`;
                                            })()}{' '}
                                            para o próximo ciclo.
                                        </span>
                                    </BannerContent>
                                    <BannerButtons>
                                        <Button
                                            primary
                                            data-testid="banner-credits-depleted-cta"
                                            onClick={() => setShowCreditPurchaseModal(true)}
                                        >
                                            Comprar créditos
                                        </Button>
                                    </BannerButtons>
                                </Banner>
                            )}
                            {isOwner && subscriptionBannerInfo?.type === 'overdue' && (
                                <Banner variant="error" data-testid="banner-overdue">
                                    <BannerContent>
                                        <BannerTitle>🚨 A cobrança está vencida</BannerTitle>
                                        <span>
                                            Caso não seja paga em {subscriptionBannerInfo.daysUntilShutdown}{' '}
                                            {subscriptionBannerInfo.daysUntilShutdown === 1 ? 'dia' : 'dias'}, seus
                                            clientes não agendam mais pelo link nem recebem lembretes.
                                        </span>
                                    </BannerContent>
                                    <BannerButtons>
                                        {subscriptionBannerInfo.invoiceUrl && (
                                            <Button
                                                as="a"
                                                href={subscriptionBannerInfo.invoiceUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={() => gtagEvent('banner_pay_click', { source: 'overdue' })}
                                            >
                                                Pagar cobrança
                                            </Button>
                                        )}
                                    </BannerButtons>
                                </Banner>
                            )}
                            {isOwner && showConnectWhatsAppBanner && (
                                <Banner variant="warning" data-testid="banner-connect-whatsapp">
                                    <BannerContent>
                                        <BannerTitle>⚠️ Conecte seu WhatsApp</BannerTitle>
                                        <span>
                                            Seu plano está ativo! Conecte o número do estabelecimento para enviar
                                            lembretes por WhatsApp e ativar o assistente virtual.
                                        </span>
                                    </BannerContent>
                                    <BannerButtons>
                                        <Button primary onClick={handleConnectWhatsAppClick}>
                                            Conectar agora
                                        </Button>
                                    </BannerButtons>
                                </Banner>
                            )}
                            {isOwner &&
                                canUseConnector &&
                                showWhatsAppInvalidatedBanner &&
                                business?.whatsapp?.type === 'connector' && (
                                    <Banner variant="error" data-testid="banner-whatsapp-invalidated">
                                        <BannerContent>
                                            <BannerTitle>⚠️ O WhatsApp desconectou o computador</BannerTitle>
                                            <span>
                                                Abra o OrganizaBot Conector no computador e escaneie o novo QR code com
                                                o WhatsApp do negócio para o assistente voltar a responder.
                                            </span>
                                        </BannerContent>
                                        <BannerButtons>
                                            <Button primary onClick={handleReconnectWhatsAppClick}>
                                                Ver como reconectar
                                            </Button>
                                        </BannerButtons>
                                    </Banner>
                                )}
                            {isOwner && showWhatsAppInvalidatedBanner && business?.whatsapp?.type === 'cloud_api' && (
                                <Banner variant="error" data-testid="banner-whatsapp-invalidated">
                                    <BannerContent>
                                        <BannerTitle>⚠️ Conexão com o WhatsApp expirou</BannerTitle>
                                        <span>
                                            A autorização desse número junto à Meta foi removida ou expirou. Reconecte
                                            para voltar a enviar mensagens e usar o assistente.
                                        </span>
                                    </BannerContent>
                                    <BannerButtons>
                                        <Button primary onClick={handleReconnectWhatsAppClick}>
                                            Reconectar agora
                                        </Button>
                                    </BannerButtons>
                                </Banner>
                            )}
                            {isOwner && whatsAppBillingBlock && (
                                <Banner variant="error" data-testid="banner-whatsapp-billing-block">
                                    <BannerContent>
                                        <BannerTitle>
                                            ⚠️ A Meta parou de entregar as respostas do assistente
                                        </BannerTitle>
                                        <span>
                                            Seu WhatsApp usou as mensagens gratuitas do mês e a conta na Meta não tem
                                            cartão cadastrado. O assistente fica pausado com os clientes até você
                                            cadastrar um cartão ou o mês virar.
                                        </span>
                                    </BannerContent>
                                    <BannerButtons>
                                        <Button
                                            primary
                                            as="a"
                                            href={whatsAppBillingBlock.billingUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={handleWhatsAppBillingBlockClick}
                                        >
                                            Cadastrar cartão na Meta
                                        </Button>
                                    </BannerButtons>
                                </Banner>
                            )}
                            {isOwner && showConnectorOfflineBanner && (
                                <Banner variant="error" data-testid="banner-connector-offline">
                                    <BannerContent>
                                        <BannerTitle>⚠️ OrganizaBot Conector desconectado</BannerTitle>
                                        <span>
                                            O computador com o OrganizaBot Conector está desligado, sem internet ou com
                                            o app fechado. O assistente volta a responder assim que ele ficar online.
                                        </span>
                                    </BannerContent>
                                    <BannerButtons>
                                        <Button onClick={handleReconnectWhatsAppClick}>Ver status</Button>
                                    </BannerButtons>
                                </Banner>
                            )}
                            {isOwner && isSubscriptionInactive && (
                                <Banner data-testid="banner-subscription-inactive">
                                    <BannerContent>
                                        <BannerTitle>Assinatura inativa</BannerTitle>
                                        <span>
                                            Seus clientes não conseguem agendar pelo link e não recebem lembretes. Você
                                            continua cuidando da agenda por aqui.
                                        </span>
                                    </BannerContent>
                                    <BannerButtons>
                                        <Button primary onClick={handleReactivateClick}>
                                            Reativar
                                        </Button>
                                    </BannerButtons>
                                </Banner>
                            )}
                        </BannersContainer>
                        {isAuthenticated && (
                            <>
                                <SectionContainer visible={currentView === 'bookings'}>
                                    <BookingsSection
                                        service={_service}
                                        business={business}
                                        workers={business?.workers ?? []}
                                        services={business?.services ?? []}
                                        clients={clients}
                                        isLoadingClients={isLoadingClients}
                                        clientsLoadError={clientsLoadError}
                                        ensureClientsLoaded={ensureClientsLoaded}
                                        onReloadClients={reloadClients}
                                        businessLoadError={businessLoadError}
                                        onReloadBusiness={loadBusiness}
                                        isLoadingBusiness={isLoadingBusiness}
                                        modalSegments={route.modalSegments}
                                        onNavigate={(segments, replace) => pushRoute('bookings', segments, replace)}
                                        confirmationEnabled={business?.bookingConfirmation !== null}
                                        checkInEnabled={business?.checkInEnabled ?? false}
                                        isOwner={isOwner}
                                        currentWorkerId={currentWorkerId}
                                    />
                                </SectionContainer>

                                <SectionContainer visible={currentView === 'clients'}>
                                    <ClientsSection
                                        service={_service}
                                        business={business}
                                        clients={clients}
                                        isLoadingClients={isLoadingClients}
                                        clientsLoadError={clientsLoadError}
                                        ensureClientsLoaded={ensureClientsLoaded}
                                        onClientsChange={reloadClients}
                                        isActive={currentView === 'clients'}
                                        modalSegments={route.modalSegments}
                                        onNavigate={(segments, replace) => pushRoute('clients', segments, replace)}
                                        isOwner={isOwner}
                                    />
                                </SectionContainer>

                                <SectionContainer visible={currentView === 'services'}>
                                    <ServicesSection
                                        service={_service}
                                        services={business?.services ?? []}
                                        intakes={business?.intakes}
                                        isReady={business !== null}
                                        businessLoadError={businessLoadError}
                                        onReloadBusiness={loadBusiness}
                                        isLoadingBusiness={isLoadingBusiness}
                                        isActive={currentView === 'services'}
                                        modalSegments={route.modalSegments}
                                        onNavigate={(segments, replace) => pushRoute('services', segments, replace)}
                                        onReload={loadBusiness}
                                    />
                                </SectionContainer>

                                <SectionContainer visible={currentView === 'workers'}>
                                    <WorkersSection
                                        service={_service}
                                        workers={business?.workers ?? []}
                                        services={business?.services ?? []}
                                        businessOpeningHours={business?.openingHours ?? null}
                                        legacyWorkersLimit={business?.legacyWorkersLimit}
                                        isActive={currentView === 'workers'}
                                        isReady={business !== null}
                                        businessLoadError={businessLoadError}
                                        onReloadBusiness={loadBusiness}
                                        isLoadingBusiness={isLoadingBusiness}
                                        modalSegments={route.modalSegments}
                                        onNavigate={(segments, replace) => pushRoute('workers', segments, replace)}
                                        onReload={loadBusiness}
                                        hasWhatsAppAccess={business ? hasPaidAccess(business) : false}
                                        hasWhatsAppConfigured={!!business?.whatsapp}
                                    />
                                </SectionContainer>

                                <SectionContainer visible={currentView === 'stats'}>
                                    <StatsSection
                                        service={_service}
                                        business={business}
                                        workers={business?.workers ?? []}
                                        services={business?.services ?? []}
                                        modalSegments={route.modalSegments}
                                        onNavigate={(segments, replace) => pushRoute('stats', segments, replace)}
                                        onNavigateToUpgrade={navigateToUpgrade}
                                    />
                                </SectionContainer>

                                <SectionContainer visible={currentView === 'assistant'}>
                                    <AssistantSection
                                        business={business}
                                        service={_service}
                                        onReloadBusiness={loadBusiness}
                                        isLoadingBusiness={isLoadingBusiness}
                                        businessLoadError={businessLoadError}
                                        isActive={currentView === 'assistant'}
                                        isTrialEligible={business?.subscription?.isTrialEligible ?? false}
                                        modalSegments={route.modalSegments}
                                        onNavigate={(segments, replace) => pushRoute('assistant', segments, replace)}
                                        onNavigateToUpgrade={navigateToUpgrade}
                                        whatsappOnboardingConfigId={whatsappOnboardingConfigId}
                                        aiBudgetState={aiBudgetState}
                                        onLoadAiBudget={loadAiBudget}
                                        onRefreshAiBudget={refreshAiBudget}
                                        connectorApprovedAt={connectorApprovedAt}
                                        onConnectorApprovalSettled={() => setConnectorApprovedAt(null)}
                                        onRefreshBusiness={refreshBusiness}
                                    />
                                </SectionContainer>

                                <SectionContainer visible={currentView === 'payments'}>
                                    <PaymentsSection
                                        business={business}
                                        service={_service}
                                        onReloadBusiness={loadBusiness}
                                        isLoadingBusiness={isLoadingBusiness}
                                        businessLoadError={businessLoadError}
                                        isActive={currentView === 'payments'}
                                        modalSegments={route.modalSegments}
                                        onNavigate={(segments, replace) => pushRoute('payments', segments, replace)}
                                        onNavigateToUpgrade={navigateToUpgrade}
                                    />
                                </SectionContainer>
                            </>
                        )}

                        <SectionContainer visible={currentView === 'configuration'}>
                            <ConfigurationSection
                                signOut={handleSignOut}
                                business={business}
                                service={_service}
                                businessLoadError={businessLoadError}
                                onReloadBusiness={loadBusiness}
                                isLoadingBusiness={isLoadingBusiness}
                                isActive={currentView === 'configuration'}
                                modalSegments={route.modalSegments}
                                onNavigate={(segments, replace) => pushRoute('configuration', segments, replace)}
                                whatsappOnboardingConfigId={whatsappOnboardingConfigId}
                                isOwner={isOwner}
                            />
                        </SectionContainer>
                    </ContentArea>
                </Content>
            </MainContainer>
            <Footer />
            {pendingConnectorCode && business && offersConnectorAuthorization && (
                <ConnectorAuthorizationDialog
                    userCode={pendingConnectorCode}
                    business={business}
                    isOwner={isOwner}
                    service={_service}
                    onClose={closeConnectorAuthorization}
                    onSwitchAccount={handleSignOut}
                    onApproved={() => {
                        closeConnectorAuthorization();
                        setConnectorApprovedAt(Date.now());
                        refreshBusiness();
                        pushRoute('assistant', ['config'], false);
                    }}
                />
            )}
            <CreditPurchaseModal
                service={_service}
                creditCard={business?.subscription?.creditCard ?? null}
                isOpen={showCreditPurchaseModal}
                onClose={() => {
                    setShowCreditPurchaseModal(false);
                    refreshAiBudget();
                }}
            />
        </Page>
    );
}

export default function App({ whatsappOnboardingConfigId }: { whatsappOnboardingConfigId: string }) {
    const globalStyles = <GlobalStyle />;
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(_authService.isAuthenticated);
    const [hasError, setHasError] = useState(false);
    const [business, setBusiness] = useState<BusinessDetails | null>(null);
    const [businessLoadError, setBusinessLoadError] = useState<string | null>(null);

    const tryRefresh = useCallback(async () => {
        setIsLoading(true);
        setHasError(false);
        setBusinessLoadError(null);
        try {
            const alreadyAuthenticated = _authService.isAuthenticated;
            const success = alreadyAuthenticated || (await _authService.refreshAccessToken());
            setIsAuthenticated(success);
            if (success) {
                try {
                    const data = await _service.getBusiness();
                    setBusiness(data);
                    if (data.workerId !== null) {
                        gtagIdentifyWorker({ businessId: data.id, workerId: data.workerId });
                    } else {
                        gtagIdentifyOwner({ businessId: data.id });
                    }
                } catch {
                    setBusinessLoadError('Não foi possível carregar as informações do negócio.');
                }
            }
        } catch {
            setHasError(true);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleLoginSuccess = useCallback(async () => {
        setIsAuthenticated(true);
        setIsLoading(true);
        setBusinessLoadError(null);
        try {
            const data = await _service.getBusiness();
            setBusiness(data);
            if (data.workerId !== null) {
                gtagIdentifyWorker({ businessId: data.id, workerId: data.workerId });
            } else {
                gtagIdentifyOwner({ businessId: data.id });
            }
        } catch {
            setBusinessLoadError('Não foi possível carregar as informações do negócio.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        tryRefresh();
    }, [tryRefresh]);

    useEffect(() => {
        const subscription = _authService.onSessionExpired.subscribe(() => {
            gtagClearIdentity();
            setIsAuthenticated(false);
            setBusiness(null);
            setBusinessLoadError(null);
        });
        return () => subscription.unsubscribe();
    }, []);

    // Track login page view
    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            const pageTitle = 'OrganizaBot Gestão | Login';
            document.title = pageTitle;
            gtagEvent('page_view', {
                page_title: pageTitle,
                page_location: window.location.href,
                page_path: '/',
                source_type: 'manual',
            });
            if (typeof window.fbq === 'function') {
                window.fbq('track', 'PageView');
            }
        }
    }, [isLoading, isAuthenticated]);

    if (!isAuthenticated || !business) {
        if (!isLoading && !isAuthenticated && window.location.pathname !== '/') {
            window.history.replaceState(null, '', '/');
        }
        return (
            <>
                {globalStyles}
                <LoginPage>
                    <LoginCard>
                        <CardNavbar>
                            <BrandGroup>
                                <BrandLink
                                    as="a"
                                    href="https://organizabot.com"
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Ir para a página inicial do OrganizaBot"
                                    onClick={() => gtagEvent('login_brand_click')}
                                    hoverBg="bright"
                                >
                                    <BrandLogo src="/logo_60x60.png" alt="OrganizaBot" />
                                    <strong>OrganizaBot</strong>
                                </BrandLink>
                                <Badge aria-label="Aplicação Business">Business</Badge>
                            </BrandGroup>
                            <SupportLink
                                as="a"
                                href={`https://wa.me/${appPhoneNumber}?text=Ol%C3%A1,%20preciso%20de%20ajuda%20para%20acessar%20o%20painel%20Business%20do%20OrganizaBot.`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => gtagEvent('login_support_click')}
                            >
                                Suporte
                            </SupportLink>
                        </CardNavbar>
                        {isLoading && (
                            <CardBody style={{ textAlign: 'center', padding: '40px' }}>Verificando sessão...</CardBody>
                        )}
                        {!isLoading && hasError && (
                            <CardBody style={{ textAlign: 'center', padding: '40px', alignItems: 'center' }}>
                                <div style={{ marginBottom: '12px' }}>Não foi possível verificar sua sessão.</div>
                                <Button onClick={tryRefresh}>Tentar novamente</Button>
                            </CardBody>
                        )}
                        {!isLoading && !hasError && !isAuthenticated && (
                            <LoginScreen
                                serverRootPath={serverRootPath}
                                authService={_authService}
                                onSuccess={handleLoginSuccess}
                            />
                        )}
                        {!isLoading && !hasError && isAuthenticated && businessLoadError && (
                            <CardBody style={{ textAlign: 'center', padding: '40px', alignItems: 'center' }}>
                                <div style={{ marginBottom: '12px' }}>{businessLoadError}</div>
                                <Button onClick={handleLoginSuccess}>Tentar novamente</Button>
                            </CardBody>
                        )}
                    </LoginCard>
                </LoginPage>
            </>
        );
    }

    return (
        <>
            {globalStyles}
            <ToastProvider>
                <AuthenticatedApp
                    onSignOut={() => {
                        gtagClearIdentity();
                        setIsAuthenticated(false);
                        setBusiness(null);
                    }}
                    whatsappOnboardingConfigId={whatsappOnboardingConfigId}
                    initialBusiness={business}
                />
            </ToastProvider>
        </>
    );
}
